import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import type { Page, RegistrantProfile, Role, User, UserQuery } from "../../types/user";

export function useUsers(query: UserQuery) {
  return useQuery({
    queryKey: ["users", query],
    queryFn: async () => {
      const { data } = await api.get<Page<User>>(endpoints.users.list(), { params: query });
      return data;
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await api.get<Role[]>(endpoints.roles.list());
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useUser(userId: number | null) {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      const { data } = await api.get<User>(endpoints.users.byId(String(userId)));
      return data;
    },
    enabled: userId !== null,
  });
}

export function useRegistrantProfile(userId: number | null) {
  return useQuery({
    queryKey: ["user", userId, "profile"],
    queryFn: async () => {
      const { data } = await api.get<RegistrantProfile>(
        endpoints.users.registrantProfile(String(userId)),
      );
      return data;
    },
    enabled: userId !== null,
  });
}

export interface UserPayload {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  roles: { id: number }[];
}

function useInvalidateUsers() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["users"] });
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: async (payload: UserPayload) => {
      const { data } = await api.post(endpoints.users.create(), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: async ({ userId, payload }: { userId: number; payload: UserPayload }) => {
      // No userType: on the Python service that parameter means "approve this
      // registration", which switches the default role and advances the
      // user's status. An administrator edit must not do either.
      const { data } = await api.put(endpoints.users.update(String(userId)), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

/** Approve or reject a registration. `status` goes in the query, the reason in the body. */
export function useReviewUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: async ({
      userId,
      approved,
      comment,
    }: {
      userId: number;
      approved: boolean;
      comment: string;
    }) => {
      const { data } = await api.put(
        endpoints.users.review(String(userId)),
        { comment },
        { params: { status: approved ? "APPROVED" : "REJECTED" } },
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useSetUserEnabled() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: async ({ userId, enabled }: { userId: number; enabled: boolean }) => {
      const { data } = await api.put(endpoints.users.setEnabled(String(userId)), { enabled });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await api.delete(endpoints.users.remove(String(userId)));
      return data;
    },
    onSuccess: invalidate,
  });
}

export async function downloadUserList(): Promise<Blob> {
  const { data } = await api.get<Blob>(endpoints.users.download(), { responseType: "blob" });
  return data;
}
